import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const catStyles = {
  sponsor: "border-amber-300/40 text-amber-300",
  collab: "border-sky-300/40 text-sky-300",
  media: "border-violet-300/40 text-violet-300",
  gaming: "border-rose-300/40 text-rose-300",
  tech: "border-emerald-300/40 text-emerald-300",
};

const logoWall = [
  { name: "MTV", logo: "/MTV_New_Official_Logo_2021.png" },
  { name: "Monster", logo: "/Logo_Monster_Energy (1).webp", icon: "🟢" },
  { name: "Domino's", logo: "/Dominos_pizza_logo.svg.png" },
  { name: "WWE", logo: "/wwe.png" },
  { name: "US Army", logo: "/army.png" },
  { name: "Microsoft", logo: "/Microsoft_logo_(2012).svg.png", icon: "💻" },
  { name: "NBA", logo: "/nba.png" },
  { name: "WIPR", logo: "/wipr.png" },
  { name: "PRTV+", logo: "/PRTV.png" },
  { name: "PRCC", logo: "/prcc.png" },
  { name: "Distrito T-Mobile", logo: "/Distrito_T-Mobile_Logo.png", icon: "🏙️" },
  { name: "Coca-Cola", logo: "/Coca-Cola_logo.svg.png", icon: "🔴" },
  { name: "Holsum", logo: "/holsum_logo_no_copyright-5254df3ef43833086d754021bc286de4.png", icon: "🍞" },
  { name: "Columbina", logo: "/Colombina_Logo_2x_9fffec22-93be-4984-a2b2-5b7b9f0547aa.webp", icon: "🍬" },
  { name: "Activision", logo: "/Activision-logo.png", icon: "🔫" },
  { name: "EA Sports", logo: "/EA-Sports-Logo-2005.png", icon: "🕹️" },
  { name: "2K Sports", logo: "/2K_Sports (1).svg", icon: "🎮" },
  { name: "Metro PR", logo: "/METRO-PUERTO-RICO-1.png", icon: "📰" },
  { name: "Natl. Lumber", logo: null, icon: "🏗️" },
  { name: "VH1", logo: "/VH1_logonew.svg.png" },
  { name: "+ more", logo: null, icon: "🇵🇷" },
];

function getCardLogoClass(name) {
  if (["Coca-Cola", "Periódico Metro PR", "MTV / VH1", "Monster Energy", "Holsum"].includes(name)) {
    return "w-full h-auto";
  }
  return "max-h-full max-w-full";
}

function getWallLogoClass(name) {
  if (["Microsoft"].includes(name)) return "h-5 w-auto object-contain";
  if (["Coca-Cola", "Metro PR", "MTV", "Monster", "Holsum"].includes(name)) return "h-6 w-auto object-contain";
  return "h-8 w-auto object-contain";
}

const copyByLang = {
  es: {
    title: "Aliados | Puerto Rico Esports League",
    description: "Descubre cómo marcas, instituciones y aliados estratégicos pueden colaborar con Puerto Rico Esports League a través de contenido, eventos en vivo y activaciones culturales.",
    ogDescription: "PREL conecta marcas y aliados con audiencias gaming a través de medios, eventos y experiencias en vivo desde Puerto Rico.",
    filters: [
      { key: "all", label: "Todos" },
      { key: "sponsor", label: "Patrocinadores" },
      { key: "collab", label: "Colaboradores" },
      { key: "media", label: "Medios" },
      { key: "gaming", label: "Gaming" },
      { key: "tech", label: "Tecnología" },
    ],
    heroEyebrow: "Puerto Rico Esports League — Aliados",
    heroTitle: "Alianzas construidas desde cultura, medios y experiencias en vivo.",
    heroBody: "PREL trabaja con marcas, instituciones, medios y aliados estratégicos que quieren conectar con audiencias gaming a través de televisión, contenido digital, eventos en vivo y activaciones culturalmente relevantes. Más que visibilidad, ofrecemos una plataforma donde las alianzas pueden convertirse en narrativa, experiencia y crecimiento real.",
    metrics: [
      ["30+", "Marcas y aliados"],
      ["TV + digital", "Plataforma multiformato"],
      ["Eventos en vivo", "Activaciones y experiencias"],
      ["PR / mercado hispano / LATAM", "Relevancia de mercado"],
    ],
    whyEyebrow: "Por qué trabajar con PREL",
    whyTitle: "Una plataforma con alcance, identidad y espacio para crecer",
    whyBody: "PREL se mueve entre gaming, cultura joven, televisión, entretenimiento en vivo y narrativa de marca. Esa combinación nos permite ofrecer oportunidades de patrocinio, contenido de marca, integración en medios, activaciones presenciales y conversaciones más amplias sobre visibilidad, expansión y colaboración estratégica.",
    featuredTitle: "Alianzas destacadas",
    brandsCount: "marcas",
    allEyebrow: "Todas las marcas",
    allTitle: "El ecosistema completo",
    convoEyebrow: "Conversaciones de alianza",
    convoTitle: "Construyamos algo con sentido junto a PREL",
    convoBody: "Desde activaciones en vivo y contenido de marca hasta integraciones en televisión y experiencias digitales, estamos abiertos a conversaciones con marcas, instituciones, medios y aliados estratégicos.",
    ctaPartners: "Explorar caminos de colaboración",
    ctaPress: "Ver prensa y medios",
    exploreEyebrow: "Qué pueden explorar nuestros aliados",
    exploreItems: [
      "Auspicios e integraciones de marca",
      "Activaciones en eventos y experiencias en vivo",
      "Colaboraciones en TV y contenido digital",
      "Visibilidad en medios y conexión con audiencia",
      "Conversaciones estratégicas y de expansión",
    ],
    partners: [
      { name: "Monster Energy", years: "2019 – 2023", cat: "sponsor", tag: "Patrocinador principal", logo: "/Logo_Monster_Energy (1).webp", icon: "🟢", impact: "Fue uno de los aliados ancla de PREL durante varias temporadas, respaldando torneos de alto perfil, presencia de marca en eventos y una narrativa competitiva más ambiciosa para la liga.", featured: true },
      { name: "Coca-Cola", years: "2020 – 2022", cat: "sponsor", tag: "Patrocinador", logo: "/Coca-Cola_logo.svg.png", icon: "🔴", impact: "Acompañó iniciativas de transmisión y experiencias presenciales, aportando visibilidad masiva y conexión con audiencias fuera del nicho competitivo tradicional." },
      { name: "US Army", years: "2019 – 2020", cat: "sponsor", tag: "Patrocinador", logo: "/army.png", icon: "⭐", impact: "Su apoyo temprano ayudó a validar la propuesta de PREL ante marcas de escala nacional y a consolidar formatos competitivos desde etapas iniciales." },
      { name: "Domino's Pizza", years: "2021 – 2023", cat: "sponsor", tag: "Patrocinador", logo: "/Dominos_pizza_logo.svg.png", icon: "🍕", impact: "Participó como food partner en eventos y campañas, integrando activaciones que elevaron la experiencia del público y de los jugadores." },
      { name: "Holsum", years: "2020 – 2021", cat: "sponsor", tag: "Patrocinador local", logo: "/holsum_logo_no_copyright-5254df3ef43833086d754021bc286de4.png", icon: "🍞", impact: "Una marca local reconocida que fortaleció la legitimidad de los esports frente al público general en Puerto Rico." },
      { name: "Columbina", years: "2019 – 2020", cat: "sponsor", tag: "Patrocinador", logo: "/Colombina_Logo_2x_9fffec22-93be-4984-a2b2-5b7b9f0547aa.webp", icon: "🍬", impact: "Apoyó experiencias de fan en eventos presenciales y sumó presencia de producto en momentos clave de activación." },
      { name: "Puerto Rico Retail Store (National Lumber)", years: "2022", cat: "sponsor", tag: "Patrocinador", logo: null, icon: "🏗️", impact: "Respaldó necesidades de infraestructura y operación para elevar la ejecución técnica de eventos y venues." },
      { name: "WWE", years: "2019 – 2021", cat: "collab", tag: "Colaborador", logo: "/wwe.png", icon: "🏟️", impact: "La integración de experiencias gaming dentro de eventos WWE ayudó a abrir nuevas audiencias y a mostrar el potencial de PREL en entretenimiento crossover.", featured: true },
      { name: "Tercer Hombre (Comic-Con PR)", years: "2021 – 2023", cat: "collab", tag: "Colaborador", logo: "/prcc.png", icon: "🎭", impact: "Una alianza estratégica que conectó la cultura geek y competitiva, posicionando zonas gaming PREL como punto fuerte dentro del evento." },
      { name: "Distrito T-Mobile", years: "2021 – 2023", cat: "collab", tag: "Venue / Colaborador", logo: "/Distrito_T-Mobile_Logo.png", icon: "🏙️", impact: "Su espacio ayudó a convertir eventos y activaciones de PREL en experiencias presenciales con mayor visibilidad, tráfico y valor de marca dentro del ecosistema local." },
      { name: "NBA", years: "2020", cat: "collab", tag: "Colaborador", logo: "/nba.png", icon: "🏀", impact: "Activaciones relacionadas a NBA 2K reforzaron el puente entre deporte tradicional y esports en la isla." },
      { name: "MTV / VH1", years: "2019 – 2020", cat: "media", tag: "Medios", logo: "/MTV_New_Official_Logo_2021.png", icon: "📺", impact: "La exposición en canales de entretenimiento popular ayudó a amplificar el alcance cultural de PREL más allá de la comunidad core." },
      { name: "WIPR", years: "2020 – 2023", cat: "media", tag: "Televisión / Radio Pública", logo: "/wipr.png", icon: "📡", impact: "Su respaldo institucional en TV y radio pública fue clave para legitimar el ecosistema esports ante audiencias amplias en Puerto Rico." },
      { name: "Periódico Metro PR", years: "2021 – 2023", cat: "media", tag: "Prensa / medios", logo: "/METRO-PUERTO-RICO-1.png", icon: "📰", impact: "Cobertura editorial consistente que elevó la conversación de esports en medios generalistas." },
      { name: "FIFA / EA Sports", years: "2020 – 2023", cat: "gaming", tag: "Gaming", logo: "/EA-Sports-Logo-2005.png", icon: "⚽", impact: "Sus iniciativas impulsaron el circuito de fútbol virtual en Puerto Rico mediante torneos y presencia de marca sostenida." },
      { name: "2K Sports", years: "2019 – 2022", cat: "gaming", tag: "Gaming", logo: "/2K_Sports (1).svg", icon: "🎮", impact: "La integración de títulos competitivos clave como NBA 2K y WWE 2K ayudó a fortalecer la continuidad y variedad del calendario competitivo." },
      { name: "Activision", years: "2020 – 2021", cat: "gaming", tag: "Gaming", logo: "/Activision-logo.png", icon: "🔫", impact: "Aportó visibilidad y activos oficiales para experiencias de Call of Duty dentro del circuito local." },
      { name: "Electronic Arts", years: "2021 – 2023", cat: "gaming", tag: "Gaming", logo: "/EA-Sports-Logo-2005.png", icon: "🕹️", impact: "El apoyo multi-título fortaleció la percepción regional de PREL como plataforma competitiva con proyección caribeña." },
      { name: "Microsoft", years: "2020 – 2023", cat: "tech", tag: "Tecnología", logo: "/Microsoft_logo_(2012).svg.png", icon: "💻", impact: "Soporte tecnológico para operación, infraestructura digital y escalabilidad de experiencias." },
    ],
  },
  en: {
    title: "Partners | Puerto Rico Esports League",
    description: "Discover how brands, institutions, and strategic partners can collaborate with Puerto Rico Esports League through content, live events, and culturally relevant activations.",
    ogDescription: "PREL connects brands and partners with gaming audiences through media, events, and live experiences from Puerto Rico.",
    filters: [
      { key: "all", label: "All" },
      { key: "sponsor", label: "Sponsors" },
      { key: "collab", label: "Collaborators" },
      { key: "media", label: "Media" },
      { key: "gaming", label: "Gaming" },
      { key: "tech", label: "Technology" },
    ],
    heroEyebrow: "Puerto Rico Esports League — Partners",
    heroTitle: "Partnerships built from culture, media, and live experiences.",
    heroBody: "PREL works with brands, institutions, media, and strategic partners that want to connect with gaming audiences through television, digital content, live events, and culturally relevant activations. Beyond visibility, we offer a platform where partnerships can turn into narrative, experience, and real growth.",
    metrics: [
      ["30+", "Brands and partners"],
      ["TV + digital", "Multi-format platform"],
      ["Live events", "Activations and experiences"],
      ["PR / Hispanic market / LATAM", "Market relevance"],
    ],
    whyEyebrow: "Why work with PREL",
    whyTitle: "A platform with reach, identity, and room to grow",
    whyBody: "PREL moves across gaming, youth culture, television, live entertainment, and brand storytelling. That combination allows us to offer sponsorship opportunities, branded content, media integration, in-person activations, and broader conversations about visibility, expansion, and strategic collaboration.",
    featuredTitle: "Featured partnerships",
    brandsCount: "brands",
    allEyebrow: "All brands",
    allTitle: "The full ecosystem",
    convoEyebrow: "Partnership conversations",
    convoTitle: "Let's build something meaningful with PREL",
    convoBody: "From live activations and branded content to TV integrations and digital experiences, we are open to conversations with brands, institutions, media, and strategic partners.",
    ctaPartners: "Explore collaboration paths",
    ctaPress: "View press and media",
    exploreEyebrow: "What partners can explore with us",
    exploreItems: [
      "Sponsorships and brand integrations",
      "Event activations and live experiences",
      "TV and digital content collaborations",
      "Media visibility and audience connection",
      "Strategic and expansion conversations",
    ],
    partners: [
      { name: "Monster Energy", years: "2019 – 2023", cat: "sponsor", tag: "Lead sponsor", logo: "/Logo_Monster_Energy (1).webp", icon: "🟢", impact: "It was one of PREL's anchor partners across multiple seasons, backing high-profile tournaments, event brand presence, and a more ambitious competitive narrative for the league.", featured: true },
      { name: "Coca-Cola", years: "2020 – 2022", cat: "sponsor", tag: "Sponsor", logo: "/Coca-Cola_logo.svg.png", icon: "🔴", impact: "It supported broadcast initiatives and in-person experiences, bringing broad visibility and connection with audiences outside the traditional competitive niche." },
      { name: "US Army", years: "2019 – 2020", cat: "sponsor", tag: "Sponsor", logo: "/army.png", icon: "⭐", impact: "Its early support helped validate PREL's proposal with nationally scaled brands and solidify competitive formats from the initial stages." },
      { name: "Domino's Pizza", years: "2021 – 2023", cat: "sponsor", tag: "Sponsor", logo: "/Dominos_pizza_logo.svg.png", icon: "🍕", impact: "It joined as a food partner in events and campaigns, integrating activations that elevated the audience and player experience." },
      { name: "Holsum", years: "2020 – 2021", cat: "sponsor", tag: "Local sponsor", logo: "/holsum_logo_no_copyright-5254df3ef43833086d754021bc286de4.png", icon: "🍞", impact: "A well-known local brand that strengthened the legitimacy of esports in front of Puerto Rico's general audience." },
      { name: "Columbina", years: "2019 – 2020", cat: "sponsor", tag: "Sponsor", logo: "/Colombina_Logo_2x_9fffec22-93be-4984-a2b2-5b7b9f0547aa.webp", icon: "🍬", impact: "It supported fan experiences at in-person events and added product presence during key activation moments." },
      { name: "Puerto Rico Retail Store (National Lumber)", years: "2022", cat: "sponsor", tag: "Sponsor", logo: null, icon: "🏗️", impact: "It backed infrastructure and operations needs that helped raise the technical execution of events and venues." },
      { name: "WWE", years: "2019 – 2021", cat: "collab", tag: "Collaborator", logo: "/wwe.png", icon: "🏟️", impact: "Integrating gaming experiences inside WWE events helped open new audiences and show PREL's potential within crossover entertainment.", featured: true },
      { name: "Tercer Hombre (Comic-Con PR)", years: "2021 – 2023", cat: "collab", tag: "Collaborator", logo: "/prcc.png", icon: "🎭", impact: "A strategic alliance that connected geek and competitive culture, positioning PREL gaming zones as a strong point inside the event." },
      { name: "Distrito T-Mobile", years: "2021 – 2023", cat: "collab", tag: "Venue / Collaborator", logo: "/Distrito_T-Mobile_Logo.png", icon: "🏙️", impact: "Its venue helped turn PREL events and activations into in-person experiences with greater visibility, foot traffic, and brand value inside the local ecosystem." },
      { name: "NBA", years: "2020", cat: "collab", tag: "Collaborator", logo: "/nba.png", icon: "🏀", impact: "NBA 2K-related activations strengthened the bridge between traditional sports and esports on the island." },
      { name: "MTV / VH1", years: "2019 – 2020", cat: "media", tag: "Media", logo: "/MTV_New_Official_Logo_2021.png", icon: "📺", impact: "Exposure through popular entertainment channels helped amplify PREL's cultural reach beyond the core community." },
      { name: "WIPR", years: "2020 – 2023", cat: "media", tag: "Public TV / Radio", logo: "/wipr.png", icon: "📡", impact: "Its institutional backing in public TV and radio was key to legitimizing the esports ecosystem in front of broad audiences in Puerto Rico." },
      { name: "Periódico Metro PR", years: "2021 – 2023", cat: "media", tag: "Press / media", logo: "/METRO-PUERTO-RICO-1.png", icon: "📰", impact: "Consistent editorial coverage that elevated the esports conversation in mainstream media." },
      { name: "FIFA / EA Sports", years: "2020 – 2023", cat: "gaming", tag: "Gaming", logo: "/EA-Sports-Logo-2005.png", icon: "⚽", impact: "Its initiatives helped drive the virtual football circuit in Puerto Rico through tournaments and sustained brand presence." },
      { name: "2K Sports", years: "2019 – 2022", cat: "gaming", tag: "Gaming", logo: "/2K_Sports (1).svg", icon: "🎮", impact: "The integration of key competitive titles like NBA 2K and WWE 2K helped strengthen the continuity and variety of the competitive calendar." },
      { name: "Activision", years: "2020 – 2021", cat: "gaming", tag: "Gaming", logo: "/Activision-logo.png", icon: "🔫", impact: "It brought visibility and official assets for Call of Duty experiences inside the local circuit." },
      { name: "Electronic Arts", years: "2021 – 2023", cat: "gaming", tag: "Gaming", logo: "/EA-Sports-Logo-2005.png", icon: "🕹️", impact: "Its multi-title support strengthened the regional perception of PREL as a competitive platform with Caribbean projection." },
      { name: "Microsoft", years: "2020 – 2023", cat: "tech", tag: "Technology", logo: "/Microsoft_logo_(2012).svg.png", icon: "💻", impact: "Technology support for operations, digital infrastructure, and the scalability of experiences." },
    ],
  },
};

export default function PartnersPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const copy = copyByLang[lang];
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    document.title = copy.title;

    const setMeta = (name, content, isProperty = false) => {
      const selector = isProperty ? `meta[property='${name}']` : `meta[name='${name}']`;
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) tag.setAttribute("property", name);
        else tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };

    setMeta("description", copy.description);
    setMeta("og:title", copy.title, true);
    setMeta("og:description", copy.ogDescription, true);
    setMeta("og:type", "website", true);
    setMeta("og:url", "https://www.puertoricoesportleague.com/partners", true);
    setMeta("og:image", "https://www.puertoricoesportleague.com/logo.png", true);

    let canonical = document.head.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.puertoricoesportleague.com/partners");

    const schemaId = "prel-partners-schema";
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement("script");
      schemaTag.type = "application/ld+json";
      schemaTag.id = schemaId;
      document.head.appendChild(schemaTag);
    }
    schemaTag.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: copy.title,
      url: "https://www.puertoricoesportleague.com/partners",
      description: copy.description,
      about: {
        "@type": "Organization",
        name: "Puerto Rico Esports League",
        url: "https://www.puertoricoesportleague.com",
      },
    });
  }, [copy]);

  const visiblePartners = useMemo(
    () => copy.partners.filter((p) => activeFilter === "all" || p.cat === activeFilter),
    [activeFilter, copy.partners]
  );

  return (
    <div className="min-h-screen bg-[#06070A] text-[#F0EEE8]">
      <SiteHeader current="/partners" />

      <section className="max-w-[1500px] mx-auto px-6 xl:px-8 pt-12 pb-14">
        <p className="text-xs tracking-[0.3em] uppercase text-[#CE1126]">{copy.heroEyebrow}</p>
        <h1 className="mt-4 text-[clamp(2.6rem,9vw,7rem)] font-black leading-[0.9] uppercase">{copy.heroTitle}</h1>
        <p className="mt-5 max-w-4xl text-slate-300 leading-relaxed">{copy.heroBody}</p>

        <div className="mt-9 grid grid-cols-2 md:grid-cols-4 border border-white/10 bg-[#121826]">
          {copy.metrics.map(([value, label]) => (
            <article key={label} className="p-5 border-r border-b md:border-b-0 border-white/10 last:border-r-0">
              <p className="text-3xl font-black">{value}</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mt-2">{label}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="sticky top-[73px] z-30 border-y border-white/10 bg-[#0f1220]/95 backdrop-blur px-6 xl:px-8">
        <div className="max-w-[1500px] mx-auto flex gap-2 overflow-x-auto py-2">
          {copy.filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`px-4 py-2 text-xs tracking-[0.18em] uppercase border transition whitespace-nowrap ${
                activeFilter === f.key ? "border-[#CE1126] text-white bg-[#CE1126]/15" : "border-white/15 text-slate-400 hover:text-white hover:border-white/35"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      <main className="max-w-[1500px] mx-auto px-6 xl:px-8 py-10">
        <div className="mb-8 max-w-4xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.whyEyebrow}</p>
          <h2 className="text-2xl md:text-4xl font-black uppercase mt-2">{copy.whyTitle}</h2>
          <p className="text-slate-300 mt-3 leading-relaxed">{copy.whyBody}</p>
        </div>
        <div className="flex items-end justify-between mb-6">
          <h2 className="text-2xl md:text-4xl font-black uppercase">{copy.featuredTitle}</h2>
          <p className="text-xs tracking-[0.2em] uppercase text-slate-400">{visiblePartners.length} {copy.brandsCount}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visiblePartners.map((p) => (
            <article key={p.name} className={`border border-white/10 bg-[#141828] p-6 relative overflow-hidden hover:border-[#CE1126]/40 transition rounded-[24px] ${p.featured ? "lg:col-span-2" : ""}`}>
              <div className="w-16 h-16 border border-white/10 bg-[#0F1220] flex items-center justify-center text-2xl overflow-hidden p-2">
                {p.logo ? <img src={p.logo} alt={`${p.name} logo`} className={`${getCardLogoClass(p.name)} object-contain`} loading="lazy" /> : <span>{p.icon}</span>}
              </div>
              <span className={`inline-block mt-4 text-[10px] tracking-[0.2em] uppercase border px-2.5 py-1 ${catStyles[p.cat]}`}>{p.tag}</span>
              <h3 className={`mt-3 font-black uppercase ${p.featured ? "text-3xl" : "text-xl"}`}>{p.name}</h3>
              <p className="mt-3 text-slate-300 leading-relaxed">{p.impact}</p>
              <div className="absolute left-0 bottom-0 h-[2px] w-full bg-[#CE1126]/80" />
            </article>
          ))}
        </div>

        <div className="mt-14 border-t border-white/10 pt-10">
          <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126] mb-2">{copy.allEyebrow}</p>
          <h3 className="text-2xl font-black uppercase mb-6">{copy.allTitle}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
            {logoWall.map((item) => (
              <div key={item.name} className="h-20 border border-white/10 bg-[#121826] flex items-center justify-center text-xs tracking-[0.14em] uppercase text-slate-300 text-center px-3 hover:border-[#CE1126]/40 transition">
                <div className="flex items-center justify-center gap-2">
                  {item.logo ? <img src={item.logo} alt={`${item.name} logo`} className={getWallLogoClass(item.name)} loading="lazy" /> : <><span className="text-base">{item.icon}</span><span>{item.name}</span></>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      <section className="border-t border-white/10 bg-[#0F1220]">
        <div className="max-w-[1500px] mx-auto px-6 xl:px-8 py-16 grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CE1126]">{copy.convoEyebrow}</p>
            <h3 className="mt-2 text-3xl md:text-5xl font-black uppercase leading-[0.95]">{copy.convoTitle}</h3>
            <p className="mt-4 max-w-2xl text-slate-300 leading-relaxed">{copy.convoBody}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/for-partners" className="px-6 py-3 bg-[#CE1126] text-white font-semibold hover:bg-[#a50e1e] transition rounded-xl">{copy.ctaPartners}</a>
              <a href="/press" className="px-6 py-3 border border-white/15 text-white hover:bg-white/5 transition rounded-xl">{copy.ctaPress}</a>
            </div>
          </div>

          <div className="border border-white/10 bg-[#111722] p-6 md:p-7 rounded-[24px]">
            <p className="text-xs uppercase tracking-[0.3em] text-cyan-300">{copy.exploreEyebrow}</p>
            <ul className="mt-4 space-y-3 text-slate-300 leading-relaxed text-sm md:text-base">
              {copy.exploreItems.map((item) => <li key={item}>• {item}</li>)}
            </ul>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
